import { useEffect as useEffectPricing } from "react";
import { Icon, Logo } from "./components";

// Public pricing & marketing page. Reachable from the auth screen
// "See pricing" link (which also sets ?page=pricing in the URL so
// this page is shareable). Renders only when the user is signed out;
// signed-in users see app routes instead.
//
// Pricing source of truth: ../pricing.md (gitignored internal doc).
// When pricing.md changes, update the numbers here too. The page
// frames Pro as "$0.50/door" rather than exposing the underlying
// $0.50/unit * unit_count formula — same math, friendlier label.

function PricingPage({ onBack }) {
  // Reset scroll on mount so a deep CTA scroll from a prior session
  // doesn't leave the user mid-page.
  useEffectPricing(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pricing">
      {/* ─── Top bar ──────────────────────────────────────────────── */}
      <header className="pricing__topbar">
        <button
          type="button"
          className="pricing__brand"
          onClick={onBack}
          title="Back to sign in"
        >
          <Logo size={28} />
          <span className="pricing__brand-name">Community Compass</span>
        </button>
        <div className="pricing__topbar-right">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onBack}
          >
            ← Sign in
          </button>
          <button
            type="button"
            className="btn btn--accent btn--sm"
            onClick={() => onBack("community")}
            title="Onboard an HOA"
          >
            Get started
          </button>
        </div>
      </header>

      {/* ─── Hero ─────────────────────────────────────────────────── */}
      <section className="pricing__hero">
        <div className="pricing__hero-inner">
          <div className="pricing__eyebrow">For HOAs of every size</div>
          <h1 className="pricing__hero-title">
            One subscription. Your whole community, <em>finally</em> on one page.
          </h1>
          <p className="pricing__hero-lede">
            Bylaws answered in plain English with the citation right there.
            A neighbor-only marketplace, services board, and calendar so
            your community stops living in a Facebook group it doesn't
            control. The HOA pays one monthly bill — residents pay nothing.
          </p>
          <div className="pricing__hero-cta">
            <button
              type="button"
              className="btn btn--accent"
              onClick={() => onBack("community")}
            >
              Start a 14-day free trial
            </button>
            <a className="pricing__hero-secondary" href="#tiers">See plans ↓</a>
          </div>
          <div className="pricing__hero-badges">
            <div className="pricing__badge">
              <Icon name="shield" />
              <span>No card required to start a trial</span>
            </div>
            <div className="pricing__badge">
              <Icon name="users" />
              <span>Residents never pay Compass</span>
            </div>
            <div className="pricing__badge">
              <Icon name="chat" />
              <span>Cancel anytime, monthly billing</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── What you get ─────────────────────────────────────────── */}
      <section className="pricing__section pricing__section--light">
        <div className="pricing__container">
          <h2 className="pricing__h2">What every plan includes</h2>
          <p className="pricing__h2-sub">
            Whether your HOA has 30 doors or 3,000, every plan ships with the full feature set.
          </p>
          <div className="pricing__features">
            <div className="pricing__feature">
              <div className="pricing__feature-icon"><Icon name="shield" /></div>
              <h3>Bylaws, decoded</h3>
              <p>
                Residents ask <em>"can I paint my front door teal?"</em> in plain English.
                Answers cite the exact section, page, and quoted line of your CC&Rs.
                Upload a revision and every answer reflects it the next day.
              </p>
            </div>
            <div className="pricing__feature">
              <div className="pricing__feature-icon"><Icon name="file" /></div>
              <h3>Project Builder</h3>
              <p>
                ARC requests written, polished, and submitted in minutes — not weeks.
                Owners describe what they want, the assistant turns it into a board-ready
                application that already knows your bylaws.
              </p>
            </div>
            <div className="pricing__feature">
              <div className="pricing__feature-icon"><Icon name="users" /></div>
              <h3>Neighbor-only marketplace</h3>
              <p>
                Sell the patio set, find a babysitter, swap tool loans — without
                broadcasting to the whole city. Listings stay inside your community.
              </p>
            </div>
            <div className="pricing__feature">
              <div className="pricing__feature-icon"><Icon name="calendar" /></div>
              <h3>Shared calendar & messages</h3>
              <p>
                Board meetings, pool hours, social events — and private messages
                between neighbors. No more "did anyone see the email about…"
              </p>
            </div>
            <div className="pricing__feature">
              <div className="pricing__feature-icon"><Icon name="bell" /></div>
              <h3>Real notifications</h3>
              <p>
                A bell that surfaces what actually matters — new messages, ARC
                approvals, community events. Each resident controls what pings them.
              </p>
            </div>
            <div className="pricing__feature">
              <div className="pricing__feature-icon"><Icon name="trend" /></div>
              <h3>Manager dashboard</h3>
              <p>
                See what residents are actually asking, which bylaws need clarifying,
                and which projects are queued. Quiet visibility, no spying.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Self-managed pricing tiers ───────────────────────────── */}
      <section id="tiers" className="pricing__section">
        <div className="pricing__container">
          <h2 className="pricing__h2">Self-managed HOAs</h2>
          <p className="pricing__h2-sub">
            Three plans, same features. Pick by community size — switch anytime.
            <strong> Residents always use Compass for free.</strong>
          </p>

          <div className="pricing__tiers">
            <article className="pricing__tier">
              <div className="pricing__tier-name">Starter</div>
              <div className="pricing__tier-price">
                <span className="pricing__tier-price-amount">$39</span>
                <span className="pricing__tier-price-period">/mo</span>
              </div>
              <div className="pricing__tier-tagline">Up to 78 units</div>
              <ul className="pricing__tier-feats">
                <li>Every feature unlocked</li>
                <li>50 polished projects/month</li>
                <li>Unlimited resident chat</li>
                <li>Up to 5 household members per door</li>
                <li>Email support</li>
              </ul>
              <button
                type="button"
                className="btn btn--ghost pricing__tier-cta"
                onClick={() => onBack("community")}
              >
                Start free trial
              </button>
              <div className="pricing__tier-foot">Best for small HOAs and condos.</div>
            </article>

            <article className="pricing__tier pricing__tier--featured">
              <div className="pricing__tier-flag">Most common</div>
              <div className="pricing__tier-name">Pro</div>
              <div className="pricing__tier-price">
                <span className="pricing__tier-price-amount">$0.50</span>
                <span className="pricing__tier-price-period">/door/mo</span>
              </div>
              <div className="pricing__tier-tagline">78 to 500 units · ~$39–$249/mo</div>
              <ul className="pricing__tier-feats">
                <li>Everything in Starter</li>
                <li>Polish budget scales with your size</li>
                <li>Priority email support</li>
                <li>Operator-approved unit-count changes mid-cycle</li>
                <li>Bylaw versioning</li>
              </ul>
              <button
                type="button"
                className="btn btn--accent pricing__tier-cta"
                onClick={() => onBack("community")}
              >
                Start free trial
              </button>
              <div className="pricing__tier-foot">
                You only pay for the doors you actually have.
              </div>
            </article>

            <article className="pricing__tier">
              <div className="pricing__tier-name">Estate</div>
              <div className="pricing__tier-price">
                <span className="pricing__tier-price-amount">$249</span>
                <span className="pricing__tier-price-period">/mo</span>
              </div>
              <div className="pricing__tier-tagline">500+ units (price ceiling)</div>
              <ul className="pricing__tier-feats">
                <li>Everything in Pro</li>
                <li>Generous polish budget, scales with size</li>
                <li>Phone support during business hours</li>
                <li>Best per-door rate available</li>
                <li>Roadmap input</li>
              </ul>
              <button
                type="button"
                className="btn btn--ghost pricing__tier-cta"
                onClick={() => onBack("community")}
              >
                Start free trial
              </button>
              <div className="pricing__tier-foot">
                Talk to us if you're managing a portfolio of HOAs — see PMC pricing below.
              </div>
            </article>
          </div>

          <div className="pricing__note">
            <strong>What a "polish" is:</strong> when a resident drafts an ARC
            application, the assistant rewrites and structures it into a
            board-ready submission. Casual chat questions don't count. Most
            communities use far fewer polishes than their plan allows.
          </div>
        </div>
      </section>

      {/* ─── PMC pricing ──────────────────────────────────────────── */}
      <section className="pricing__section pricing__section--accent">
        <div className="pricing__container">
          <div className="pricing__eyebrow pricing__eyebrow--light">Property management companies</div>
          <h2 className="pricing__h2 pricing__h2--light">Portfolio pricing with volume breaks.</h2>
          <p className="pricing__h2-sub pricing__h2-sub--light">
            One platform fee, one invoice, every HOA you manage — with rates that
            drop as the portfolio grows. Built for the operations director, not
            the homeowner.
          </p>

          <div className="pricing__pmc-card">
            <div className="pricing__pmc-platform">
              <div className="pricing__pmc-platform-label">Platform fee</div>
              <div className="pricing__pmc-platform-amount">$99<span>/mo</span></div>
              <div className="pricing__pmc-platform-sub">One per company, regardless of portfolio size</div>
            </div>

            <div className="pricing__pmc-tiers">
              <div className="pricing__pmc-tier-head">+ Per-household, by total portfolio size</div>
              <table className="pricing__pmc-table">
                <thead>
                  <tr><th>Units in portfolio</th><th>Rate per household / month</th></tr>
                </thead>
                <tbody>
                  <tr><td>First 5,000 units</td><td><strong>$0.85</strong></td></tr>
                  <tr><td>Next 15,000 units (5,001 – 20,000)</td><td><strong>$0.65</strong></td></tr>
                  <tr><td>Above 20,000 units</td><td><strong>$0.50</strong></td></tr>
                </tbody>
              </table>
              <div className="pricing__pmc-setup">
                One-time setup fee: <strong>$1,500</strong> (onboarding, training, white-glove migration of your existing HOA roster)
              </div>
            </div>
          </div>

          <h3 className="pricing__h3 pricing__h3--light">Worked examples</h3>
          <div className="pricing__pmc-examples">
            <div className="pricing__pmc-example">
              <div className="pricing__pmc-example-name">Small PMC</div>
              <div className="pricing__pmc-example-detail">5 HOAs × ~80 units = 400 doors</div>
              <div className="pricing__pmc-example-calc mono">400 × $0.85 + $99 platform</div>
              <div className="pricing__pmc-example-total">$439<span>/mo</span></div>
            </div>
            <div className="pricing__pmc-example">
              <div className="pricing__pmc-example-name">Mid-market</div>
              <div className="pricing__pmc-example-detail">30 HOAs × ~80 units = 2,400 doors</div>
              <div className="pricing__pmc-example-calc mono">2,400 × $0.85 + $99 platform</div>
              <div className="pricing__pmc-example-total">$2,139<span>/mo</span></div>
            </div>
            <div className="pricing__pmc-example">
              <div className="pricing__pmc-example-name">Regional</div>
              <div className="pricing__pmc-example-detail">75 HOAs × ~100 units = 7,500 doors</div>
              <div className="pricing__pmc-example-calc mono">(5K × $0.85) + (2.5K × $0.65) + $99</div>
              <div className="pricing__pmc-example-total">$5,974<span>/mo</span></div>
            </div>
            <div className="pricing__pmc-example">
              <div className="pricing__pmc-example-name">Enterprise</div>
              <div className="pricing__pmc-example-detail">200+ HOAs · 20,000+ doors</div>
              <div className="pricing__pmc-example-calc">Custom pricing at this scale</div>
              <a
                className="pricing__pmc-example-contact"
                href="mailto:Mike@communitycompass.ai?subject=Enterprise%20portfolio%20pricing"
              >
                Contact for pricing →
              </a>
            </div>
          </div>

          <div className="pricing__pmc-feats">
            <div className="pricing__pmc-feat">
              <Icon name="file" />
              <div>
                <b>Portfolio dashboard.</b> See every HOA's usage, ARC volume,
                and bylaw-question topics in one switcher. No tabs to juggle.
              </div>
            </div>
            <div className="pricing__pmc-feat">
              <Icon name="users" />
              <div>
                <b>Central billing.</b> One Stripe invoice for the whole portfolio
                instead of one per community. Accounting will thank you.
              </div>
            </div>
            <div className="pricing__pmc-feat">
              <Icon name="shield" />
              <div>
                <b>Dedicated CSM + 99.5% SLA.</b> A real human you can call, and
                uptime commitments in your contract. Quarterly business reviews.
              </div>
            </div>
            <div className="pricing__pmc-feat">
              <Icon name="chat" />
              <div>
                <b>SSO & white-label.</b> Enterprise sales conversation — we'll
                tailor a contract that fits the way you sell to your boards.
              </div>
            </div>
          </div>

          <div className="pricing__pmc-cta">
            <button
              type="button"
              className="btn btn--accent"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.location.href = "mailto:Mike@communitycompass.ai?subject=Portfolio%20demo%20request";
                }
              }}
            >
              Talk to sales
            </button>
            <div className="pricing__pmc-cta-foot">
              We'll respond within one business day. Most portfolios are live in under two weeks.
            </div>
          </div>
        </div>
      </section>

      {/* ─── For residents ────────────────────────────────────────── */}
      <section className="pricing__section pricing__section--light">
        <div className="pricing__container">
          <h2 className="pricing__h2">What your residents actually get</h2>
          <p className="pricing__h2-sub">
            The HOA pays one bill. Every resident — primary or family member — uses Compass for free, forever.
          </p>

          <div className="pricing__resident-grid">
            <div className="pricing__resident-card">
              <div className="pricing__resident-card-name">Primary account</div>
              <div className="pricing__resident-card-tag">One per household</div>
              <ul>
                <li>Full bylaw Q&A with citations</li>
                <li>Project Builder for ARC requests</li>
                <li>Marketplace, services, assistance boards</li>
                <li>Direct messages and calendar</li>
                <li>Member directory and notifications</li>
              </ul>
            </div>
            <div className="pricing__resident-card">
              <div className="pricing__resident-card-name">Family members</div>
              <div className="pricing__resident-card-tag">Up to 5 per household</div>
              <ul>
                <li>Read the bylaws library</li>
                <li>Calendar and member directory</li>
                <li>Marketplace, services, assistance</li>
                <li>Direct messages</li>
                <li>(AI features stay with the primary)</li>
              </ul>
            </div>
            <div className="pricing__resident-card">
              <div className="pricing__resident-card-name">Renter-occupied units</div>
              <div className="pricing__resident-card-tag">Optional transfer</div>
              <ul>
                <li>Owner can hand primary access to the tenant</li>
                <li>Tenant gets the full Compass experience</li>
                <li>Owner can take it back when the lease ends</li>
                <li>HOA billing doesn't change — same door, same rate</li>
                <li>Self-serve from household settings</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────── */}
      <section className="pricing__section">
        <div className="pricing__container pricing__container--narrow">
          <h2 className="pricing__h2">Common questions</h2>

          <div className="pricing__faq">
            <details>
              <summary>How does the free trial work?</summary>
              <p>
                14 days, every feature unlocked, no card required. The trial
                has a generous polish budget so you can demo the Project
                Builder to your board. If you need more time, you can extend
                once for another 7 days from inside the app. Convert when
                you're ready — no awkward "your trial expired" lockout.
              </p>
            </details>
            <details>
              <summary>What happens if our unit count changes mid-year?</summary>
              <p>
                Adding doors (annexation, phase 2) is automatic — the next
                invoice reflects the new count. Removing doors goes through
                a quick operator review so we can sanity-check the change,
                then your bill drops on the following cycle. No mid-month
                proration drama either way.
              </p>
            </details>
            <details>
              <summary>Who at the HOA can see what residents asked the assistant?</summary>
              <p>
                Nobody. Resident chat history is private to the resident.
                Managers see aggregate usage (how many questions, which
                bylaw topics are hottest) but never individual transcripts.
                That's a hard product line, not a setting.
              </p>
            </details>
            <details>
              <summary>What does Compass do when our bylaws change?</summary>
              <p>
                Upload the new version from the manager dashboard. The
                system re-indexes it within minutes, and every answer the
                next day reflects the new wording — with citations pointed
                at the new section numbers. Old versions stay readable in
                the library so residents can compare.
              </p>
            </details>
            <details>
              <summary>Do residents have to install anything?</summary>
              <p>
                No. Compass is a web app — works on phones, tablets, and
                computers. Residents sign up with their email, the HOA
                approves them, and they're in. No app store downloads.
              </p>
            </details>
            <details>
              <summary>Can we cancel anytime?</summary>
              <p>
                Yes. Monthly billing, cancel with one click from the
                manager dashboard. Your data exports cleanly if you ever
                need to leave — bylaws PDFs are still yours, member
                directory is downloadable, no hostage tactics.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ────────────────────────────────────────────── */}
      <section className="pricing__cta-strip">
        <div className="pricing__container">
          <h2 className="pricing__cta-title">Ready to give your HOA a real home?</h2>
          <p className="pricing__cta-sub">
            14-day trial. No card up front. Your board will be using it by next meeting.
          </p>
          <div className="pricing__cta-actions">
            <button
              type="button"
              className="btn btn--accent"
              onClick={() => onBack("community")}
            >
              Onboard our HOA
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => onBack("login")}
            >
              I have an account
            </button>
          </div>
        </div>
      </section>

      {/* ─── Footer ───────────────────────────────────────────────── */}
      <footer className="pricing__footer">
        <div className="pricing__container pricing__footer-row">
          <div className="pricing__footer-brand">
            <Logo size={20} />
            <span>Community Compass</span>
          </div>
          <div className="pricing__footer-links">
            <button type="button" onClick={onBack}>Sign in</button>
            <a href="mailto:Mike@communitycompass.ai">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { PricingPage };
